import React, {useMemo} from 'react';

import {ProcedureStepNumber} from 'app/procedures/components/ProcedureStepNumber';
import {StepName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {AssignableItemStructure} from './AssignableItemStructure';
import {AssignableLaneWithStepType} from './AssignableLanes';
import {AssignFormAssignToListStructure} from './AssignableListStructure';

import {applyChangeToAssignment, hasAnyAssignee} from '../utils';

import type {AssigneesByType, SetAssignmentsType} from '../types';
import type {
  AnyStepType,
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  RegularProcessProcedureAsStepSerializedType,
} from 'sweet-types';

export type AssignableStepType =
  | ProcedureContentStepType
  | ProcedureDecisionStepType
  | RegularProcessProcedureAsStepSerializedType;

interface Props {
  steps: Array<AnyStepType>;
  lanes: Array<AssignableLaneWithStepType>;
  assignments: {
    [type in 'lanes' | 'steps']: {[id: string]: AssigneesByType};
  };
  setAssignments: SetAssignmentsType;
}

export function AssignFormAssignToSteps({
  steps,
  lanes,
  assignments,
  setAssignments,
}: Props) {
  const assignableSteps: Array<AssignableStepType> = useMemo(
    () =>
      steps.filter(
        (step): step is AssignableStepType =>
          step.type == null ||
          step.type == 'decision' ||
          step.type === 'content' ||
          (step.type === 'processprocedure' && !step.deleted)
      ),
    [steps]
  );

  const assignedViaLanes = useMemo(
    () =>
      lanes.reduce((map, l) => {
        for (const step of l.stepsInLane) {
          map[step.id] = assignments.lanes[l.lane.hashid];
        }

        return map;
      }, {}),
    [lanes, assignments.lanes]
  );

  return assignableSteps.length > 0 ? (
    <AssignFormAssignToListStructure>
      {({assigning, setAssigning, ListHeading}) => (
        <>
          <ListHeading>{gt('Steps')}</ListHeading>
          {assignableSteps.map((step) => (
            <AssignFormAssignToStepItem
              key={step.id}
              step={step}
              assigning={assigning === step.id}
              assignees={assignments.steps[step.id]}
              assigneesViaLane={assignedViaLanes[step.id]}
              setAssigning={(assigning: boolean) =>
                setAssigning(assigning ? step.id : null)
              }
              setAssignments={setAssignments}
            />
          ))}
        </>
      )}
    </AssignFormAssignToListStructure>
  ) : (
    <div className="alert alert-warning my-4" role="alert">
      {gt('This document has no steps')}
    </div>
  );
}

export function AssignFormAssignToStepItem({
  step,
  assigning,
  assignees,
  assigneesViaLane,
  setAssigning,
  setAssignments,
}: {
  step: AssignableStepType;
  assigning: boolean;
  setAssigning: (assigning: boolean) => unknown;
  assignees?: AssigneesByType;
  assigneesViaLane?: AssigneesByType;
  setAssignments: SetAssignmentsType;
}): JSX.Element {
  const hasLaneAssignees = useMemo(
    () => hasAnyAssignee(assigneesViaLane),
    [assigneesViaLane]
  );
  const hasDirectAssignees = useMemo(
    () => hasAnyAssignee(assignees),
    [assignees]
  );

  return (
    <AssignableItemStructure
      icon={<ProcedureStepNumber n={step.n} type={step.type} />}
      title={<StepName step={step} />}
      assigning={assigning}
      assignees={assignees}
      immutableAssignees={assigneesViaLane}
      setAssigning={setAssigning}
      onChange={(meta, ctname) =>
        applyChangeToAssignment(
          meta,
          ctname,
          'step_id',
          step.id,
          setAssignments
        )
      }
      footer={
        hasLaneAssignees ? (
          <div className="text-right font-italic small text-secondary">
            {hasDirectAssignees
              ? gt('some assigned via lane')
              : gt('assigned via lane')}
          </div>
        ) : null
      }
    />
  );
}
